import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MdAdd, MdArrowBack, MdClose, MdEdit } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import {
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  TableBodySkeleton,
} from '@euroasiann/shared/ui';

const PAGE_SIZE = 10;

type SurveyorStatus = 'active' | 'inactive';

interface SocietyRef {
  _id: string;
  name: string;
  shortCode: string;
  status: 'active' | 'inactive';
}

interface Surveyor {
  _id: string;
  societyId?: SocietyRef | string;
  surveyorName: string;
  surveyorEmail: string;
  phone?: string;
  status: SurveyorStatus;
}

interface SurveyorFormData {
  surveyorName: string;
  surveyorEmail: string;
  phone: string;
  status: SurveyorStatus;
}

const INITIAL_FORM: SurveyorFormData = {
  surveyorName: '',
  surveyorEmail: '',
  phone: '',
  status: 'active',
};

function parseListPayload(payload: any): Surveyor[] {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

export function ClassificationSurveyorsPage() {
  const { societyId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [surveyors, setSurveyors] = useState<Surveyor[]>([]);
  const [society, setSociety] = useState<SocietyRef | null>(
    (location.state as any)?.society || null
  );
  const [statusTab, setStatusTab] = useState<SurveyorStatus>('active');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSurveyor, setEditingSurveyor] = useState<Surveyor | null>(null);
  const [formData, setFormData] = useState<SurveyorFormData>(INITIAL_FORM);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!societyId) return;
    void fetchSociety();
  }, [societyId]);

  useEffect(() => {
    if (!societyId) return;
    void fetchSurveyors();
  }, [societyId, statusTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusTab, societyId]);

  const totalPages = Math.max(1, Math.ceil(surveyors.length / PAGE_SIZE));
  const paginatedSurveyors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return surveyors.slice(start, start + PAGE_SIZE);
  }, [surveyors, currentPage]);

  const fetchSociety = async () => {
    try {
      const response = await authenticatedFetch(`/api/v1/admin/classification-societies/${societyId}`);
      if (!response.ok) return;
      const payload = await response.json();
      if (payload?.data) setSociety(payload.data);
    } catch {
      // Keep page functional even if heading fallback is used.
    }
  };

  const fetchSurveyors = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(
        `/api/v1/admin/classification-societies/${societyId}/surveyors?status=${statusTab}&limit=200`
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch surveyors');
      }
      const payload = await response.json();
      setSurveyors(parseListPayload(payload));
    } catch (error: any) {
      showToast(error.message || 'Failed to fetch surveyors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSurveyor(null);
    setFormData(INITIAL_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = (surveyor: Surveyor) => {
    setEditingSurveyor(surveyor);
    setFormData({
      surveyorName: surveyor.surveyorName,
      surveyorEmail: surveyor.surveyorEmail,
      phone: surveyor.phone || '',
      status: surveyor.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSurveyor(null);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async () => {
    if (!formData.surveyorName.trim() || !formData.surveyorEmail.trim()) {
      showToast('Surveyor name and email are required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = editingSurveyor
        ? `/api/v1/admin/classification-surveyors/${editingSurveyor._id}`
        : `/api/v1/admin/classification-societies/${societyId}/surveyors`;
      const method = editingSurveyor ? 'PUT' : 'POST';

      const response = await authenticatedFetch(endpoint, {
        method,
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save surveyor');
      }

      showToast(
        editingSurveyor ? 'Surveyor updated successfully' : 'Surveyor created successfully',
        'success'
      );
      closeModal();
      await fetchSurveyors();
    } catch (error: any) {
      showToast(error.message || 'Failed to save surveyor', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (surveyor: Surveyor) => {
    if (!window.confirm(`Set surveyor "${surveyor.surveyorName}" to inactive?`)) return;
    try {
      const response = await authenticatedFetch(`/api/v1/admin/classification-surveyors/${surveyor._id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to deactivate surveyor');
      }
      showToast('Surveyor set to inactive', 'success');
      await fetchSurveyors();
    } catch (error: any) {
      showToast(error.message || 'Failed to deactivate surveyor', 'error');
    }
  };

  return (
    <div className="w-full min-h-screen p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/dashboard/admin/classification-societies')}
            className="mb-3 inline-flex items-center gap-2 text-sm text-[hsl(var(--primary))] hover:underline"
          >
            <MdArrowBack className="w-4 h-4" />
            Back to societies
          </button>
          <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
            <span className={erpPageBreadcrumbItemClass}>Admin</span>
            <span className={erpPageBreadcrumbSepClass}>/</span>
            <span className={erpPageBreadcrumbCurrentClass}>Classification</span>
          </nav>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">
            Surveyors {society ? `- ${society.shortCode}` : ''}
          </h1>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors"
        >
          <MdAdd className="w-5 h-5" />
          Add Surveyor
        </button>
      </div>

      <div className="mb-6 flex gap-2 border-b border-[hsl(var(--border))]">
        <button
          onClick={() => setStatusTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusTab === 'active'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setStatusTab('inactive')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusTab === 'inactive'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Inactive
        </button>
      </div>

      <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[hsl(var(--secondary))]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[hsl(var(--card))] divide-y divide-[#0088cc]/20 dark:divide-gray-800">
              {loading ? (
                <TableBodySkeleton rows={6} cols={5} />
              ) : surveyors.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-[hsl(var(--muted-foreground))]">
                    No surveyors found.
                  </td>
                </tr>
              ) : (
                paginatedSurveyors.map((surveyor) => (
                  <tr key={surveyor._id}>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">{surveyor.surveyorName}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{surveyor.surveyorEmail}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{surveyor.phone || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        surveyor.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {surveyor.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(surveyor)}
                          className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400"
                          title="Edit"
                        >
                          <MdEdit className="w-5 h-5" />
                        </button>
                        {surveyor.status === 'active' && (
                          <button
                            onClick={() => handleDeactivate(surveyor)}
                            className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                            title="Deactivate"
                          >
                            <MdClose className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {surveyors.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Page {currentPage} of {totalPages} · {surveyors.length} total
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-xl mx-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">
                {editingSurveyor ? 'Edit Surveyor' : 'Add Surveyor'}
              </h2>
              <button onClick={closeModal} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] dark:hover:text-gray-200">
                <MdClose className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Surveyor Name</label>
                <input
                  type="text"
                  value={formData.surveyorName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, surveyorName: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Surveyor Email</label>
                <input
                  type="email"
                  value={formData.surveyorEmail}
                  onChange={(e) => setFormData((prev) => ({ ...prev, surveyorEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone (Optional)</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              {editingSurveyor && (
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as SurveyorStatus }))}
                    className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                  >
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-[#0088cc] text-[hsl(var(--primary))] rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : editingSurveyor ? 'Update Surveyor' : 'Add Surveyor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

